import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal
} from '@angular/core';
import {
  AppRoutes,
  CopyValueToClipboardDirective,
  GameDetailsModel,
  GameParticipationStatusLabel,
  gameParticipationStatusLabelMapper,
  GamesRoutes,
  HostedGamesRoutes,
  isPrivateGame,
  privateGameLabel
} from '@core';
import { Router } from '@angular/router';
import { RedirectTypeEnum } from '../enums/redirect-type.enum';
import { BadgeModeEnum } from '../enums/badge-mode.enum';
import { OrAvatarComponent } from '../../or-avatar/or-avatar.component';
import { ShareGameDirective } from '../../share-game';
import { UserParticipationStatus } from '@core-http';
import { OrIconComponent } from '../../or-icon/or-icon.component';

@Component({
  selector: 'lib-game-card-v2',
  templateUrl: './game-card-v2.component.html',
  styleUrls: ['./game-card-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [OrAvatarComponent, ShareGameDirective, CopyValueToClipboardDirective, OrIconComponent]
})
export class GameCardV2Component {
  readonly game: InputSignal<GameDetailsModel> = input.required<GameDetailsModel>();
  readonly redirectType: InputSignal<RedirectTypeEnum | null> = input<RedirectTypeEnum | null>(null);
  readonly badgeMode: InputSignal<BadgeModeEnum> = input<BadgeModeEnum>(BadgeModeEnum.NONE);
  readonly showBottomDetails: InputSignal<boolean> = input<boolean>(true);
  readonly showShareButton: InputSignal<boolean> = input<boolean>(false);
  readonly showAddSimilarGame: InputSignal<boolean> = input<boolean>(false);
  readonly showHostDetails: InputSignal<boolean> = input<boolean>(false);
  readonly showPhoneNumber: InputSignal<boolean> = input<boolean>(false);
  readonly isAdmin: InputSignal<boolean | null> = input<boolean | null>(false);
  readonly marginsEnabled: InputSignal<boolean | null> = input<boolean | null>(true);

  readonly onAddSimilarGameClicked: OutputEmitterRef<void> = output<void>();

  private readonly router: Router = inject(Router);

  readonly hostDetailsVisible: Signal<boolean> = computed<boolean>(
    () => this.showHostDetails() && !this.game().host?.isAnonymous
  );

  readonly cardBadge: Signal<GameParticipationStatusLabel | null> = computed<GameParticipationStatusLabel | null>(
    () => {
      switch (this.badgeMode()) {
        case BadgeModeEnum.HOST:
          return this.waitingPlayersBadge();
        case BadgeModeEnum.PLAYER:
          return this.participationBadge() ?? this.privateBadge();
        default:
          return null;
      }
    }
  );

  readonly isPrivateGame: Signal<boolean> = computed<boolean>(() => isPrivateGame(this.game()));

  readonly isAnonymizedGame: Signal<boolean> = computed<boolean>(() => this.isPrivateGame() && !this.isAdmin());

  readonly hostRatingValue: Signal<string | null> = computed<string | null>(() => {
    if (!this.game().host?.rating.hostRating) return null;
    return this.game().host?.rating.hostRating ?? null;
  });

  goToDetails(): void {
    if (this.isAnonymizedGame()) return;

    const type: RedirectTypeEnum | null = this.redirectType();
    if (!type) return;

    if (type === RedirectTypeEnum.GAME) {
      void this.router.navigate([`${AppRoutes.GAMES}/${GamesRoutes.DETAILS}/${this.game().gameId}`]);
    }

    if (type === RedirectTypeEnum.HOSTED) {
      void this.router.navigate([`${AppRoutes.HOST}/${HostedGamesRoutes.FUTURE}/${this.game().gameId}`]);
    }
  }

  similarGameClicked(event: MouseEvent): void {
    event.stopImmediatePropagation();
    this.onAddSimilarGameClicked.emit();
  }

  private waitingPlayersBadge(): GameParticipationStatusLabel | null {
    const waitingCount: number = this.game().playerApplications?.length ?? 0;
    if (!waitingCount) return null;

    return {
      iconName: 'game-status-waiting-host',
      displayName: `Oczekujących: ${waitingCount}`,
      bgColor: '#FFF3E0',
      textColor: '#FF9800'
    };
  }

  private participationBadge(): GameParticipationStatusLabel | null {
    const status: UserParticipationStatus | undefined = this.game().gameParticipationStatus;
    if (!status || !this.isParticipationStatusDisplayed(status)) return null;

    return gameParticipationStatusLabelMapper[status] ?? null;
  }

  private privateBadge(): GameParticipationStatusLabel | null {
    return this.isPrivateGame() ? privateGameLabel : null;
  }

  private isParticipationStatusDisplayed(status: UserParticipationStatus): boolean {
    return (
      status === UserParticipationStatus.Applied ||
      status === UserParticipationStatus.Approved ||
      status === UserParticipationStatus.Invited
    );
  }
}
